// apps/web/src/auth/RequirePageAccess.tsx
// WHY: Route guard for pages gated by a pageAccess PERMISSION rather than a
// role (e.g. the dark-by-default AI Spend Tracker). The dashboard is hidden for
// every non-owner role until an admin grants '/ai-spend' page access in the
// Roles UI. Owner holds '*' so it always resolves. Falls back to redirect (not
// lockout) when permissions are unavailable.

import { Navigate } from 'react-router-dom';
import { Skeleton, Stack } from '@mantine/core';

import { useAuth } from './auth-context';
import { usePermissions, useHasPageAccess } from '../hooks/usePermissions';

interface RequirePageAccessProps {
  /** The pageAccess path pattern this route requires (e.g. '/ai-spend'). */
  path: string;
  children: React.ReactNode;
}

export function RequirePageAccess({ path, children }: RequirePageAccessProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: permLoading } = usePermissions();
  const allowed = useHasPageAccess(path);

  if (authLoading || permLoading) {
    return (
      <Stack p="xl" gap="md">
        <Skeleton height={40} />
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // WHY: No page-access grant → redirect home (dark by default).
  if (!allowed) return <Navigate to="/" replace />;

  return <>{children}</>;
}
